import { campaignWarn } from './diagnostics';
import {
  CampaignEntryStyle,
  CampaignGreeting,
  CampaignPriority,
  EvaluationCandidate,
  EvaluationResult,
  ParsedClaim,
} from './types';

/**
 * IMBEE-8954 (REQ-CWC-04) — "Unknown or malformed campaign responses degrade to 'no campaign'".
 *
 * This is the ONE place a raw evaluate/claim response is trusted, and it is written against the
 * published api-document (`api-doc/web-chat-service.visitor.openapi.json` — external to this repo;
 * `apiDocumentFixtures.ts` states where it lives and which copy is authoritative), which is the
 * only statement of the contract there is: both responses are wrapped in `data`, the candidate
 * carries `delaySeconds`/`requiresExitIntent` (there is no `trigger` field on the wire), `priority`
 * is a string enum, and greeting content is `data.greeting.{title,body,sender:{name,avatarUrl}}`.
 *
 * Anything that doesn't match is dropped rather than thrown, so a rollback-era mismatch (old
 * widget vs new backend, or the reverse) reads as "nothing to show" instead of a crash — but every
 * drop is reported through `diagnostics.ts`, because a mismatch that produces the same silence as
 * "no campaign matched" is the failure mode nobody can see.
 */

const PRIORITIES: CampaignPriority[] = ['high', 'normal', 'low'];
const ENTRY_STYLES: CampaignEntryStyle[] = ['teaser', 'autoOpen'];
const SUPPRESSION_REASONS = ['suppressedNoAgent', 'suppressedCap', 'suppressedPriority'];

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : null;

/** Both responses put the payload under `data` — the estate's canonical envelope. */
const unwrapData = (raw: unknown): Record<string, unknown> | null => {
  const envelope = asRecord(raw);
  if (!envelope) return null;
  return asRecord(envelope.data);
};

const EMPTY_EVALUATION: EvaluationResult = { sessionKey: null, candidates: [] };

/**
 * Read-only evaluate response → the server's ORDERED candidate list, order preserved. A candidate
 * missing any of `Candidate`'s five required properties is dropped rather than kept half-formed:
 * a candidate with no `delaySeconds` could arm a timer that never fires, and one with no
 * `campaignId` could be claimed with an id the server does not recognise.
 */
export const parseEvaluateResponse = (raw: unknown): EvaluationResult => {
  const data = unwrapData(raw);
  if (!data) {
    campaignWarn('evaluate.unreadable', { received: typeof raw });
    return EMPTY_EVALUATION;
  }
  const { sessionKey, candidates } = data;
  if (!Array.isArray(candidates)) {
    campaignWarn('evaluate.candidatesNotAnArray', { received: typeof candidates });
    return EMPTY_EVALUATION;
  }

  const parsed: EvaluationCandidate[] = [];
  for (const entry of candidates) {
    const value = asRecord(entry);
    if (!value) {
      campaignWarn('evaluate.candidateNotAnObject', { received: typeof entry });
      continue;
    }
    const { campaignId, priority, delaySeconds, requiresExitIntent, entryStyle } = value;

    const missing: string[] = [];
    if (typeof campaignId !== 'string' || !campaignId) missing.push('campaignId');
    if (typeof priority !== 'string' || !PRIORITIES.includes(priority as CampaignPriority)) missing.push('priority');
    if (typeof delaySeconds !== 'number' || !isFinite(delaySeconds) || delaySeconds < 0) missing.push('delaySeconds');
    if (typeof requiresExitIntent !== 'boolean') missing.push('requiresExitIntent');
    if (typeof entryStyle !== 'string' || !ENTRY_STYLES.includes(entryStyle as CampaignEntryStyle)) {
      missing.push('entryStyle');
    }
    if (missing.length) {
      campaignWarn('evaluate.candidateDropped', { campaignId, invalidFields: missing });
      continue;
    }

    parsed.push({
      campaignId: campaignId as string,
      priority: priority as CampaignPriority,
      delaySeconds: delaySeconds as number,
      requiresExitIntent: requiresExitIntent as boolean,
      entryStyle: entryStyle as CampaignEntryStyle,
    });
  }

  return {
    sessionKey: typeof sessionKey === 'string' ? sessionKey : null,
    candidates: parsed,
  };
};

const suppressed = (reason?: unknown): ParsedClaim =>
  typeof reason === 'string' && SUPPRESSION_REASONS.includes(reason) ? { mode: 'suppressed', reason } : { mode: 'suppressed' };

const parseGreeting = (raw: unknown): CampaignGreeting | null => {
  const greeting = asRecord(raw);
  if (!greeting) return null;
  const sender = asRecord(greeting.sender);
  const { title, body } = greeting;
  const senderName = sender ? sender.name : undefined;
  if (typeof title !== 'string' || !title) return null;
  if (typeof body !== 'string' || !body) return null;
  // NOT a rejection: the sender name is optional (PRD AC-1.1). An unnamed greeting renders with no
  // attribution — returning null here folded a show the service had already recorded, and for which
  // a cap slot was spent, into `suppressed` (S11b B3).
  const avatarUrl = sender ? sender.avatarUrl : undefined;
  return {
    title,
    body,
    ...(typeof senderName === 'string' && senderName ? { senderName } : {}),
    ...(typeof avatarUrl === 'string' && avatarUrl ? { avatarUrl } : {}),
  };
};

/**
 * The claim response → the widget's own render-state input. Only an explicit `mode: 'live'`
 * carrying a `showId` AND a complete greeting is trusted as "live" — a `'suppressed'` verdict, an
 * absent/renamed field, or any third value for `mode` (there is no fallback/offline mode in v3.0 —
 * REQ-CWC-03 scenario "No offline/fallback state exists") all fold into the same `suppressed`
 * result the widget already renders nothing for. Per the api-document's `oneOf`, a suppressed
 * claim carries NO `showId` at all, which is why nothing here reads one on that branch.
 */
export const parseClaimResponse = (raw: unknown): ParsedClaim => {
  const data = unwrapData(raw);
  if (!data) {
    campaignWarn('claim.unreadable', { received: typeof raw });
    return { mode: 'suppressed' };
  }
  if (data.mode === 'suppressed') return suppressed(data.reason);
  if (data.mode !== 'live') {
    campaignWarn('claim.unrecognisedMode', { mode: data.mode });
    return { mode: 'suppressed' };
  }

  const { showId, entryStyle, skipPreChatForm } = data;
  if (typeof showId !== 'string' || !showId) {
    campaignWarn('claim.liveWithoutShowId', {});
    return { mode: 'suppressed' };
  }

  const greeting = parseGreeting(data.greeting);
  if (!greeting) {
    campaignWarn('claim.liveWithoutGreeting', { showId });
    return { mode: 'suppressed' };
  }

  return {
    mode: 'live',
    showId,
    entryStyle: entryStyle === 'autoOpen' ? 'autoOpen' : 'teaser',
    greeting,
    // Read strictly, and anything that is not the boolean `true` resolves to `false` — the same
    // shape-over-truthiness rule `entryStyle` above uses. A server that has not yet published this
    // field, or publishes `"true"`, must not be read as permission to drop the contact form: that
    // failure direction loses the visitor's details silently, while the other direction only shows
    // a form the visitor would have seen anyway.
    skipPreChatForm: skipPreChatForm === true,
  };
};
